// src/pages/admin/AdminGallery.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Folder, Trash2, Pencil, RefreshCcw } from "lucide-react";

import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";

import {
  listAdminGalleryFolders,
  createAdminGalleryFolder,
  updateAdminGalleryFolder,
  deleteAdminGalleryFolder,
} from "../../lib/adminApi";

function safeText(v) {
  return String(v ?? "").trim();
}

export default function AdminGallery() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // folder doc

  const [form, setForm] = useState({ name: "", description: "", project_slug: "" });
  const [saving, setSaving] = useState(false);

  const totalImages = useMemo(
    () => folders.reduce((acc, f) => acc + Number(f.images_count || 0), 0),
    [folders]
  );
  const totalVideos = useMemo(
    () => folders.reduce((acc, f) => acc + Number(f.videos_count || 0), 0),
    [folders]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAdminGalleryFolders();
      setFolders(res?.items || []);
    } catch (e) {
      toast({ title: "Failed to load gallery folders", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", project_slug: "" });
    setShowCreate(true);
  };

  const openEdit = (folder) => {
    setEditing(folder);
    setForm({
      name: safeText(folder?.name),
      description: safeText(folder?.description),
      project_slug: safeText(folder?.project_slug),
    });
    setShowCreate(true);
  };

  const closeModal = () => {
    setShowCreate(false);
    setEditing(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: safeText(form.name),
      description: safeText(form.description),
      project_slug: safeText(form.project_slug),
    };

    if (!payload.name) {
      toast({ title: "Folder name is required", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      if (editing?.id) {
        await updateAdminGalleryFolder(editing.id, payload);
        toast({ title: "Folder updated" });
      } else {
        await createAdminGalleryFolder(payload);
        toast({ title: "Folder created" });
      }
      closeModal();
      await load();
    } catch (e2) {
      toast({ title: "Save failed", description: e2.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeFolder = async (folder) => {
    const yes = window.confirm(`Delete folder "${folder?.name}" and all media inside?`);
    if (!yes) return;

    try {
      await deleteAdminGalleryFolder(folder.id);
      toast({ title: "Folder deleted" });
      await load();
    } catch (e) {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    }
  };

  return (
    <AdminLayout
      title="Gallery"
      onRefresh={load}
      right={
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-xl" onClick={load}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button className="rounded-xl bg-amber-700 hover:bg-amber-800" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> New Folder
          </Button>
        </div>
      }
    >
      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card className="rounded-2xl border-stone-200 shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-stone-500">Folders</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{folders.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-stone-200 shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-stone-500">Photos</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{totalImages}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-stone-200 shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-stone-500">YouTube Videos</p>
            <p className="text-2xl font-bold text-stone-900 mt-1">{totalVideos}</p>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card className="rounded-2xl border-stone-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-stone-900">Folders</h3>
            <p className="text-xs text-stone-500">Newest first</p>
          </div>

          <div className="mt-4 space-y-3">
            {loading ? (
              <div className="h-24 rounded-2xl bg-stone-100 animate-pulse" />
            ) : folders.length === 0 ? (
              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
                No folders yet. Create your first folder.
              </div>
            ) : (
              folders.map((f) => (
                <div
                  key={f.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-stone-200 p-4 hover:bg-stone-50 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center shrink-0">
                      <Folder className="w-5 h-5" />
                    </div>

                    <div className="min-w-0">
                      <p className="font-semibold text-stone-900 truncate">{f.name}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        {f.images_count || 0} photos • {f.videos_count || 0} videos • slug:{" "}
                        <span className="font-mono">{f.slug}</span>
                      </p>
                      {f.description ? (
                        <p className="text-sm text-stone-600 mt-2 line-clamp-2">{f.description}</p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button asChild variant="outline" className="rounded-xl">
                      <Link to={`/admin/gallery/${encodeURIComponent(f.slug)}`}>Open</Link>
                    </Button>

                    <Button variant="outline" className="rounded-xl" onClick={() => openEdit(f)}>
                      <Pencil className="w-4 h-4 mr-2" /> Edit
                    </Button>

                    <Button variant="destructive" className="rounded-xl" onClick={() => removeFolder(f)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            onClick={closeModal}
            aria-label="Close"
          />
          <div className="relative z-10 w-full h-full flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white border border-stone-200 shadow-2xl overflow-hidden">
              <div className="p-5 border-b border-stone-200">
                <p className="font-semibold text-stone-900">
                  {editing ? "Edit Folder" : "Create Folder"}
                </p>
                <p className="text-sm text-stone-600 mt-1">
                  Create folders per project, event, outreach, or campaign.
                </p>
              </div>

              <form onSubmit={submit} className="p-5 space-y-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Folder Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30"
                    placeholder="e.g. TATL1000 Uyo Outreach"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700">Description (optional)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30 min-h-[110px]"
                    placeholder="Short description of the folder"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700">Project Slug (optional)</label>
                  <input
                    value={form.project_slug}
                    onChange={(e) => setForm((s) => ({ ...s, project_slug: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30"
                    placeholder="If you want to link this folder to a project slug"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" className="rounded-xl" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-xl bg-amber-700 hover:bg-amber-800"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}